import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowRight, CheckCircle2, Leaf, Droplets, Wind, Map as MapIcon, Ruler, TreePine, BarChart3, Plane } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import Button from '../../components/Button/Button';
import HeroCarousel from '../../components/HeroCarousel/HeroCarousel';
import styles from './Home.module.css';

const Home = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEn = lang === 'en';

  const servicesPath = `/${lang}/${isEn ? 'services' : 'servicios'}`;
  const contactPath = `/${lang}/${isEn ? 'contact' : 'contacto'}`;
  const projectsPath = `/${lang}/${isEn ? 'projects' : 'proyectos'}`;

  return (
    <div className={styles.home}>
      <SEO 
        title={t('seo.homeTitle')}
        description={t('seo.homeDesc')}
        canonicalPath="/"
      />

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={`container ${styles.heroContainer}`}>
          <div className={styles.heroContent}>
            <span className={styles.heroBadge}>{t('home.heroBadge')}</span>
            <h1 className={styles.heroTitle}>
              <Trans i18nKey="home.heroTitle">
                Cumplimiento ambiental y monitoreo técnico con <span className={styles.highlight}>trazabilidad geoespacial</span>
              </Trans>
            </h1>
            <p className={styles.heroSubtitle}>
              {t('home.heroSubtitle')}
            </p>
            <div className={styles.heroActions}>
              <Button to={contactPath} variant="primary">{t('home.ctaPrimary')}</Button>
              <Button to={servicesPath} variant="outline">{t('home.ctaSecondary')}</Button>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <div className={styles.heroLogoWrapper}>
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className={styles.services}>
        <div className="container">
          <div className="text-center mb-4">
            <span className={styles.sectionLabel}>{t('home.servicesLabel')}</span>
            <h2>{t('home.servicesTitle')}</h2>
            <p className="text-muted">{t('home.servicesSubtitle')}</p>
          </div>
          
          <div className={styles.servicesGrid}>
            <div className={styles.serviceCard}>
              <div className={styles.iconWrapper}><Leaf size={28} /></div>
              <h3>{t('home.services.biotic.title')}</h3>
              <p>{t('home.services.biotic.desc')}</p>
              <Button to={servicesPath} variant="ghost" className="mt-2">
                {t('home.seeMore')} <ArrowRight size={16} style={{marginLeft: '4px'}} />
              </Button>
            </div>
            <div className={styles.serviceCard}>
              <div className={styles.iconWrapper}><Droplets size={28} /></div>
              <h3>{t('home.services.licensing.title')}</h3>
              <p>{t('home.services.licensing.desc')}</p>
              <Button to={servicesPath} variant="ghost" className="mt-2">
                {t('home.seeMore')} <ArrowRight size={16} style={{marginLeft: '4px'}} />
              </Button>
            </div>
            <div className={styles.serviceCard}>
              <div className={styles.iconWrapper}><Wind size={28} /></div>
              <h3>{t('home.services.climate.title')}</h3>
              <p>{t('home.services.climate.desc')}</p>
              <Button to={servicesPath} variant="ghost" className="mt-2">
                {t('home.seeMore')} <ArrowRight size={16} style={{marginLeft: '4px'}} />
              </Button>
            </div>
            <div className={styles.serviceCard}>
              <div className={styles.iconWrapper}><MapIcon size={28} /></div>
              <h3>{t('home.services.geospatial.title')}</h3>
              <p>{t('home.services.geospatial.desc')}</p>
              <Button to={servicesPath} variant="ghost" className="mt-2">
                {t('home.seeMore')} <ArrowRight size={16} style={{marginLeft: '4px'}} />
              </Button>
            </div>
          </div>
          <div className="text-center mt-4">
            <Button to={servicesPath} variant="primary">{t('home.seeAllServices')}</Button>
          </div>
        </div>
      </section>

      {/* Metrics Strip */}
      <section className={styles.metrics}>
        <div className={`container ${styles.metricsContainer}`}>
          <div className={styles.metricItem}>
            <Ruler size={24} className={styles.metricIcon} />
            <span className={styles.metricNumber}>{t('home.metrics.p1')}</span>
            <span className={styles.metricLabel}>{t('home.metrics.p1Label')}</span>
          </div>
          <div className={styles.metricItem}>
            <TreePine size={24} className={styles.metricIcon} />
            <span className={styles.metricNumber}>{t('home.metrics.p2')}</span>
            <span className={styles.metricLabel}>{t('home.metrics.p2Label')}</span>
          </div>
          <div className={styles.metricItem}>
            <BarChart3 size={24} className={styles.metricIcon} />
            <span className={styles.metricNumber}>{t('home.metrics.p3')}</span>
            <span className={styles.metricLabel}>{t('home.metrics.p3Label')}</span>
          </div>
          <div className={styles.metricItem}>
            <Plane size={24} className={styles.metricIcon} />
            <span className={styles.metricNumber}>{t('home.metrics.p4')}</span>
            <span className={styles.metricLabel}>{t('home.metrics.p4Label')}</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.features}>
        <div className="container">
          <div className={styles.featuresLayout}>
            <div className={styles.featuresText}>
              <span className={styles.sectionLabel}>{t('home.featuresLabel')}</span>
              <h2>{t('home.featuresTitle')}</h2>
              <p className="mb-3">{t('home.featuresSubtitle')}</p>
              
              <ul className={styles.featureList}>
                <li>
                  <CheckCircle2 className={styles.featureIcon} />
                  <div dangerouslySetInnerHTML={{ __html: t('home.featuresList.f1') }} />
                </li>
                <li>
                  <CheckCircle2 className={styles.featureIcon} />
                  <div dangerouslySetInnerHTML={{ __html: t('home.featuresList.f2') }} />
                </li>
                <li>
                  <CheckCircle2 className={styles.featureIcon} />
                  <div dangerouslySetInnerHTML={{ __html: t('home.featuresList.f3') }} />
                </li>
                <li>
                  <CheckCircle2 className={styles.featureIcon} />
                  <div dangerouslySetInnerHTML={{ __html: t('home.featuresList.f4') }} />
                </li>
              </ul>
            </div>
            <div className={styles.featuresImageWrapper}>
              <div className={styles.featureStats}>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>{t('home.stats.s1Number')}</span>
                  <span className={styles.statLabel}>{t('home.stats.s1Label')}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>{t('home.stats.s2Number')}</span>
                  <span className={styles.statLabel}>{t('home.stats.s2Label')}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statNumber}>{t('home.stats.s3Number')}</span>
                  <span className={styles.statLabel}>{t('home.stats.s3Label')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Preview */}
      <section className={styles.projects}>
        <div className="container">
          <div className={styles.projectsHeader}>
            <div>
              <span className={styles.sectionLabelLight}>{t('home.projectsLabel')}</span>
              <h2>{t('home.projectsTitle')}</h2>
              <p className={styles.projectsSubtitle}>{t('home.projectsSubtitle')}</p>
            </div>
            <Button to={projectsPath} variant="outline" className={styles.projectsBtn}>{t('home.projectsBtn')}</Button>
          </div>
          <div className={styles.projectsGrid}>
            <div className={styles.projectCard}>
              <div className={styles.projectCardInner}>
                <span className={styles.projectBadge}>{t('home.projects.p1Badge')}</span>
                <h4>{t('home.projects.p1Title')}</h4>
                <p className={styles.projectCategory}>{t('home.projects.p1Category')}</p>
                <p className={styles.projectDesc}>{t('home.projects.p1Desc')}</p>
                <div className={styles.projectMetrics}>
                  {t('home.projects.p1Metrics', { returnObjects: true }).map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.projectCard}>
              <div className={styles.projectCardInner}>
                <span className={`${styles.projectBadge} ${styles.inProgress}`}>{t('home.projects.p2Badge')}</span>
                <h4>{t('home.projects.p2Title')}</h4>
                <p className={styles.projectCategory}>{t('home.projects.p2Category')}</p>
                <p className={styles.projectDesc}>{t('home.projects.p2Desc')}</p>
                <div className={styles.projectMetrics}>
                  {t('home.projects.p2Metrics', { returnObjects: true }).map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.cta}>
        <div className="container text-center">
          <h2>{t('home.ctaTitle')}</h2>
          <p className="mb-3">{t('home.ctaSubtitle')}</p>
          <Button to={contactPath} variant="primary">{t('home.ctaBtn')}</Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
